import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { ROUTES } from '@/config/constants'
import { useAuthStore } from '@/store'
import LoginPage from '@/pages/LoginPage'
import Loading from '@/components/common/Loading'
import { authApi } from '@/api/auth'
import { useEffect, useState } from 'react'
import { getTelegramInitData } from '@/config/telegram'
import Toaster from '@/components/common/Toaster'

// Lazy load страниц для оптимизации размера бандла
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const DailyLogsPage = lazy(() => import('@/pages/DailyLogsPage'))
const WeeklySummariesPage = lazy(() => import('@/pages/WeeklySummariesPage'))
const TeamPage = lazy(() => import('@/pages/TeamPage'))
const AnalyticsPage = lazy(() => import('@/pages/AnalyticsPage'))
const AdminPage = lazy(() => import('@/pages/AdminPage'))
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))
const GoalsPage = lazy(() => import('@/pages/GoalsPage'))
const CreateGoalPage = lazy(() => import('@/pages/CreateGoalPage'))
const EditGoalPage = lazy(() => import('@/pages/EditGoalPage'))

const PrivateRoute = ({ children }: { children: JSX.Element }) => {
  const { isAuthenticated } = useAuthStore()
  
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />
  }
  
  return children
}

function App() {
  const { isAuthenticated, login, setLoading, logout, isLoading: isStoreLoading } = useAuthStore()
  const [isInitialized, setIsInitialized] = useState(false)

  // Проверка существующего токена при загрузке
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token')
      if (token && isAuthenticated) {
        try {
          console.log('[App] Checking existing token validity...')
          const user = await authApi.getMe()
          console.log('[App] Token is valid, user:', user)
        } catch (error) {
          console.error('[App] Token is invalid, logging out:', error)
          logout()
        }
      }
      setIsInitialized(true)
      setLoading(false)
    }
    checkAuth()
  }, [])

  // Автоматическая авторизация через Telegram initData
  useEffect(() => {
    if (!isInitialized) return

    // Проверяем наличие initData
    const initData = getTelegramInitData()
    const telegramEnv = import.meta.env.VITE_TELEGRAM
    const isTelegramMode = telegramEnv === 'true'
    
    console.log('[App] Telegram check:', {
      initDataLength: initData?.length || 0,
      isAuthenticated,
      isInitialized,
      pathname: window.location.pathname,
      isTelegramMode
    })

    if (initData && !isAuthenticated) {
      setLoading(true)
      console.log('[App] Attempting Telegram auto-login...')
      
      authApi.loginTelegram(initData)
        .then(() => {
          console.log('[App] Telegram login successful')
          // login() уже вызывается внутри loginTelegram
        })
        .catch((error) => {
          console.error('[App] Telegram login failed:', error)
        })
        .finally(() => {
          setLoading(false)
        })
    } else if (!initData && !isAuthenticated) {
      console.log('[App] No Telegram initData and not authenticated')
    }
  }, [isInitialized, isAuthenticated, login, setLoading, logout])

  // Показываем DebugPanel если есть window.Telegram (даже без initData для отладки)
  // const showDebugPanel = typeof window !== 'undefined' && !!window.Telegram?.WebApp

  if (!isInitialized || isStoreLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-page)]">
        <Loading size="lg" />
      </div>
    )
  }

  return (
    <BrowserRouter>
      <Toaster />
      <Routes>
        <Route 
          path={ROUTES.LOGIN} 
          element={isAuthenticated ? <Navigate to={ROUTES.DASHBOARD} replace /> : <LoginPage />} 
        />
        
        <Route
          path={ROUTES.DASHBOARD}
          element={
            <PrivateRoute>
              <Suspense fallback={<Loading size="lg" />}>
                <DashboardPage />
              </Suspense>
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.DAILY_LOGS}
          element={
            <PrivateRoute>
              <Suspense fallback={<Loading size="lg" />}>
                <DailyLogsPage />
              </Suspense>
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.WEEKLY_SUMMARIES}
          element={
            <PrivateRoute>
              <Suspense fallback={<Loading size="lg" />}>
                <WeeklySummariesPage />
              </Suspense>
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.TEAM}
          element={
            <PrivateRoute>
              <Suspense fallback={<Loading size="lg" />}>
                <TeamPage />
              </Suspense>
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.ANALYTICS}
          element={
            <PrivateRoute>
              <Suspense fallback={<Loading size="lg" />}>
                <AnalyticsPage />
              </Suspense>
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.ADMIN}
          element={
            <PrivateRoute>
              <Suspense fallback={<Loading size="lg" />}>
                <AdminPage />
              </Suspense>
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.PROFILE}
          element={
            <PrivateRoute>
              <Suspense fallback={<Loading size="lg" />}>
                <ProfilePage />
              </Suspense>
            </PrivateRoute>
          }
        />

        <Route
          path={ROUTES.GOALS}
          element={
            <PrivateRoute>
              <Suspense fallback={<Loading size="lg" />}>
                <GoalsPage />
              </Suspense>
            </PrivateRoute>
          }
        />

        <Route
          path={`${ROUTES.GOALS}/create`}
          element={
            <PrivateRoute>
              <Suspense fallback={<Loading size="lg" />}>
                <CreateGoalPage />
              </Suspense>
            </PrivateRoute>
          }
        />

        <Route
          path={`${ROUTES.GOALS}/:id/edit`}
          element={
            <PrivateRoute>
              <Suspense fallback={<Loading size="lg" />}>
                <EditGoalPage />
              </Suspense>
            </PrivateRoute>
          }
        />

        {/* Catch all route - redirect based on auth status */}
        <Route
          path="*"
          element={
            isAuthenticated ? (
              <Navigate to={ROUTES.DASHBOARD} replace />
            ) : (
              <Navigate to={ROUTES.LOGIN} replace />
            )
          }
        />
      </Routes>
      {/* {showDebugPanel && <DebugPanel />} */}
    </BrowserRouter>
  )
}

export default App
